<template>
  <div class="remark-modal">
    <div class="remark-header">
      <div class="company-info">
        <i class="h-icon-building" style="color: #999; margin-right: 5px;"></i>
        <span class="company-name">{{ companyName }}</span>
      </div>
    </div>
    <div class="remark-tabs">
      <div class="tab-item active">记录信息</div>
    </div>
    <div class="remark-content">
      <div class="search-bar">
        <Input 
          v-model="searchText" 
          placeholder="请输入关键字,搜索信息" 
          style="width: 100%;"
        />
      </div>
      <div class="record-list" ref="recordListRef">
        <div 
          v-for="record in filteredRecords" 
          :key="record.id" 
          class="record-item"
        >
          <div class="record-content">
            <div class="record-text">{{ record.content }}</div>
            <div v-if="record.fileUrl" class="record-files">
              <div 
                v-for="(filePath, index) in getFilePaths(record.fileUrl)" 
                :key="index"
                class="record-file-item"
                @click="downloadFile(filePath)"
              >
                <i class="h-icon-file" style="margin-right: 5px; color: #999;"></i>
                <span class="file-name">{{ getFileName(filePath) }}</span>
              </div>
            </div>
            <div class="record-meta">
              {{ record.createUser }} 创建于 {{ formatTime(record.uploadTime) }}
            </div>
          </div>
          <div class="record-action">
            <i class="h-icon-trash text-hover" @click="deleteRecord(record.id)" title="删除"></i>
          </div>
        </div>
        <div v-if="filteredRecords.length === 0" class="empty-tip">
          暂无记录
        </div>
      </div>
      <div class="add-record">
        <div class="add-record-form" :class="{ 'expanded': isExpanded }">
          <div v-if="!isExpanded" class="input-compact" @click="expandInput">
            <Input 
              v-model="newRecord" 
              placeholder="在此添加记录" 
              readonly
              style="cursor: pointer; flex: 1;"
            />
            <vxe-button status="primary" @click.stop="expandInput">提交</vxe-button>
          </div>
          <div v-else class="input-expanded">
            <div class="textarea-wrapper">
              <Textarea 
                ref="textareaRef"
                v-model="newRecord" 
                placeholder="在此添加记录" 
                :maxlength="1000"
                v-autosize
                :rows="4"
                class="record-textarea"
                @blur="handleBlur"
              />
              <div class="char-count">1000字</div>
            </div>
            <div class="upload-section">
              <div class="upload-actions-row">
                <Uploader 
                  v-model="billList" 
                  :accept="accept" 
                  :option="billUploaderOptions" 
                  :showFileList="false"
                  displayType="file" 
                  multiple
                >
                  <template #button>
                    <div class="upload-button">
                      <i class="h-icon-upload" style="margin-right: 5px;"></i>
                      <span>点击上传文件</span>
                    </div>
                  </template>
                </Uploader>
                <div class="submit-action">
                  <vxe-button status="primary" @click="addRecord" :loading="saving">提交</vxe-button>
                </div>
              </div>
              <div v-if="billList.length > 0" class="file-list">
                <div v-for="(item, index) in billList" :key="index" class="file-item">
                  <i class="h-icon-file" style="margin-right: 8px; color: #999;"></i>
                  <span class="file-name">{{ getUploadFileName(item) }}</span>
                  <i class="h-icon-trash text-hover" @click.stop="removeImage(index)" style="margin-left: 8px; color: #999; cursor: pointer;" title="删除"></i>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {mapState} from 'vuex';
import manba from 'manba';
import notePad from '@api/notePad';
import {message} from 'heyui.ext';
import setting from '@api/setting';
import Common from '@api/common';

export default {
  name: 'RemarkModal',
  props: {
    accountSetsId: {
      type: Number,
      required: true
    },
    accountDate: {
      type: String,
      required: true
    }
  },
  emits: ['close'],
  data() {
    return {
      records: [],
      searchText: '',
      newRecord: '',
      billList: [],
      saving: false,
      isExpanded: false,
      billUploaderOptions: {
        onBeforeUpload: (file) => {
          if (file.size > 10 * 1024 * 1024) {
            message.error(`${file.name} 文件大小超出10M限制`);
            return false;
          }
          return true;
        },
        onChange: async (file) => {
          try {
            const fd = new FormData();
            fd.append("file", file);
            const {data, success, msg} = await Common.upload('notepad', fd);
            if (success) {
              return data.path;
            } else {
              message.error(msg);
              throw new Error(msg);
            }
          } catch (error) {
            message.error(`${file.name}上传失败`);
            throw new Error();
          }
        }
      },
      accept: "*"
    };
  },
  computed: {
    ...mapState(['currentAccountSets', 'User']),
    companyName() {
      return this.currentAccountSets ? this.currentAccountSets.companyName : '';
    },
    filteredRecords() {
      if (!this.searchText) {
        return this.records;
      }
      const keyword = this.searchText.toLowerCase();
      return this.records.filter(record => 
        (record.content && record.content.toLowerCase().includes(keyword)) ||
        (record.createUser && record.createUser.toLowerCase().includes(keyword))
      );
    }
  },
  mounted() {
    this.loadRecords();
  },
  methods: {
    async loadRecords() {
      try {
        const {data} = await notePad.list(this.accountSetsId);
        this.records = data || [];
        this.records.sort((a, b) => {
          const timeA = a.uploadTime ? new Date(a.uploadTime).getTime() : 0;
          const timeB = b.uploadTime ? new Date(b.uploadTime).getTime() : 0;
          return timeB - timeA;
        });
      } catch (e) {
        this.records = [];
      }
    },
    getFilePaths(fileUrl) {
      if (!fileUrl) return [];
      return fileUrl.split(',').filter(path => path.trim());
    },
    getFileName(path) {
      if (!path) return '';
      if (path.includes('/')) {
        return path.substring(path.lastIndexOf('/') + 1);
      }
      return path;
    },
    async downloadFile(path) {
      if (!path) return;
      
      let url = '';
      let fileName = this.getFileName(path);
      
      if (path.startsWith('http://') || path.startsWith('https://')) {
        url = path;
        this.doDownload(url, fileName);
        return;
      }
      
      try {
        const {data, success, msg} = await setting.customerUpload.getFileUrl(path);
        
        if (success && data) {
          url = data;
          this.doDownload(url, fileName);
        } else {
          message.error(msg || '获取文件URL失败');
        }
      } catch (err) {
        message.error('获取文件URL失败');
      }
    },
    doDownload(url, fileName) {
      fetch(url)
        .then(response => {
          if (!response.ok) {
            throw new Error('下载失败');
          }
          return response.blob();
        })
        .then(blob => {
          const blobUrl = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = blobUrl;
          link.download = fileName || 'download';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(blobUrl);
        })
        .catch(err => {
          const link = document.createElement('a');
          link.href = url;
          link.target = '_blank';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          message.warning('文件下载失败，已在新窗口打开');
        });
    },
    formatTime(time) {
      if (!time) return '';
      return manba(time).format('YYYY-MM-DD HH:mm:ss');
    },
    expandInput() {
      this.isExpanded = true;
      this.$nextTick(() => {
        if (this.$refs.textareaRef) {
          this.$refs.textareaRef.$el.querySelector('textarea')?.focus();
        }
      });
    },
    handleBlur() {
      setTimeout(() => {
        if (!this.newRecord.trim() && this.billList.length === 0 && !this.saving) {
          this.isExpanded = false;
        }
      }, 200);
    },
    removeImage(index) {
      this.billList.splice(index, 1);
    },
    getUploadFileName(item) {
      if (typeof item === 'string') {
        return this.getFileName(item);
      }
      if (item && item.name) {
        return item.name;
      }
      if (item && item.url) {
        return this.getFileName(item.url);
      }
      return '未知文件';
    },
    async addRecord() {
      if (!this.newRecord.trim() && this.billList.length === 0) {
        return;
      }
      
      this.saving = true;
      try {
        let fileUrl = '';
        if (this.billList.length > 0) {
          const paths = this.billList.map(item => {
            if (typeof item === 'string') {
              return item;
            }
            if (item && item.url) {
              return item.url;
            }
            return '';
          }).filter(path => path);
          fileUrl = paths.join(',');
        }
        
        const recordData = {
          content: this.newRecord.trim() || '',
          fileUrl: fileUrl,
          createUser: this.User.realName || '系统'
        };
        
        const {data} = await notePad.save(recordData);
        
        this.records.unshift(data);
        this.newRecord = '';
        this.billList = [];
        this.isExpanded = false;
        
        this.$nextTick(() => {
          if (this.$refs.recordListRef) {
            this.$refs.recordListRef.scrollTop = 0;
          }
        });
        
        message.success('添加成功');
      } catch (e) {
        message.error('添加记录失败: ' + (e.message || '未知错误'));
      } finally {
        this.saving = false;
      }
    },
    async deleteRecord(id) {
      try {
        await notePad.delete(id, this.accountSetsId);
        const index = this.records.findIndex(r => r.id === id);
        if (index > -1) {
          this.records.splice(index, 1);
        }
        message.success('删除成功');
      } catch (e) {
        message.error('删除失败: ' + (e.message || '未知错误'));
      }
    },
  }
};
</script>

<style lang="less" scoped>
.remark-modal {
  display: flex;
  flex-direction: column;
  height: 100%;
  background-color: #ffffff;
}

.remark-header {
  padding: 15px 20px;
  border-bottom: 1px solid #e8e8e8;
  
  .company-info {
    display: flex;
    align-items: center;
    
    .company-name {
      font-size: 14px;
      color: rgba(49, 58, 70, 0.8);
      font-weight: 500;
    }
  }
}

.remark-tabs {
  display: flex;
  padding: 0 20px;
  border-bottom: 1px solid #e8e8e8;
  
  .tab-item {
    padding: 12px 0;
    margin-right: 30px;
    font-size: 14px;
    color: rgba(49, 58, 70, 0.6);
    cursor: pointer;
    border-bottom: 2px solid transparent;
    
    &.active {
      color: #1E40AF;
      border-bottom-color: #1E40AF;
    }
  }
}

.remark-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: 20px;
  overflow: hidden;
}

.search-bar {
  margin-bottom: 15px;
}

.record-list {
  flex: 1;
  overflow-y: auto;
  padding-left: 20px;
  border-left: 2px dotted #d3d3d3;
  margin-bottom: 15px;
  
  .record-item {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    padding: 12px 0;
    padding-left: 15px;
    position: relative;
    
    &::before {
      content: '';
      position: absolute;
      left: -6px;
      top: 18px;
      width: 8px;
      height: 8px;
      border-radius: 50%;
      background-color: #1E40AF;
      border: 2px solid #ffffff;
    }
    
    .record-content {
      flex: 1;
      
      .record-text {
        font-size: 14px;
        color: rgba(49, 58, 70, 0.8);
        line-height: 1.5;
        margin-bottom: 5px;
      }
      
      .record-files {
        display: flex;
        flex-direction: column;
        gap: 6px;
        margin: 8px 0;
        
        .record-file-item {
          display: flex;
          align-items: center;
          padding: 6px 10px;
          background-color: #f5f5f5;
          border-radius: 4px;
          cursor: pointer;
          transition: all 0.2s;
          
          &:hover {
            background-color: #e8e8e8;
            color: #1E40AF;
          }
          
          .file-name {
            flex: 1;
            font-size: 13px;
            color: rgba(49, 58, 70, 0.8);
          }
        }
      }
      
      .record-meta {
        font-size: 12px;
        color: rgba(49, 58, 70, 0.5);
      }
    }
    
    .record-action {
      margin-left: 15px;
      
      i {
        font-size: 16px;
        color: rgba(49, 58, 70, 0.5);
        cursor: pointer;
        
        &:hover {
          color: #f56c6c;
        }
      }
    }
  }
  
  .empty-tip {
    text-align: center;
    padding: 40px 0;
    color: rgba(49, 58, 70, 0.4);
    font-size: 14px;
  }
}

.add-record {
  padding-top: 15px;
  border-top: 1px solid #e8e8e8;
}

.add-record-form {
  background-color: #ffffff;
  border: 1px solid #e8e8e8;
  border-radius: 4px;
  padding: 12px;
  transition: all 0.3s ease;
  
  .input-compact {
    display: flex;
    align-items: center;
    gap: 10px;
  }
  
  &.expanded {
    .input-expanded {
      display: block;
    }
  }
}

.form-label {
  font-size: 14px;
  color: rgba(49, 58, 70, 0.8);
  margin-bottom: 8px;
}

.textarea-wrapper {
  position: relative;
  margin-bottom: 10px;
  
  .record-textarea {
    width: 100%;
    min-height: 100px;
    max-height: 200px;
    border: 1px solid #d3d3d3;
    border-radius: 4px;
    padding: 8px;
    font-size: 14px;
    resize: vertical;
    
    &:focus {
      border-color: #1E40AF;
      outline: none;
    }
  }
  
  .char-count {
    position: absolute;
    bottom: 6px;
    right: 10px;
    font-size: 12px;
    color: rgba(49, 58, 70, 0.5);
    background-color: #ffffff;
    padding: 2px 4px;
  }
}

.upload-section {
  margin-bottom: 10px;
  
  .upload-actions-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
  }
  
  .upload-button {
    display: inline-flex;
    align-items: center;
    padding: 8px 16px;
    border: 1px solid #d3d3d3;
    border-radius: 4px;
    cursor: pointer;
    background-color: #ffffff;
    color: rgba(49, 58, 70, 0.8);
    font-size: 14px;
    transition: all 0.2s;
    
    &:hover {
      border-color: #1E40AF;
      color: #1E40AF;
      background-color: #f5f5f5;
    }
  }
  
  .file-list {
    display: flex;
    flex-direction: column;
    gap: 6px;
    margin-top: 10px;
    
    .file-item {
      display: flex;
      align-items: center;
      padding: 6px 10px;
      background-color: #f5f5f5;
      border-radius: 4px;
      
      .file-name {
        flex: 1;
        font-size: 13px;
        color: rgba(49, 58, 70, 0.8);
      }
      
      .h-icon-trash {
        font-size: 14px;
        transition: color 0.2s;
        
        &:hover {
          color: #f56c6c;
        }
      }
    }
  }
  
  .submit-action {
    flex-shrink: 0;
  }
}
</style>
